import styled from '@emotion/styled';
import { Card, CardActionArea, useTheme } from '@mui/material';

import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Tooltip from '@/components/ToolTip/CustomTooltip';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import RadioChecked from '@/images/RadioChecked.svg';
import StopRecording from '@/images/RecordingStop.svg';
import Remove from '@/images/Remove.svg';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';

const RecordCard = styled(Card)<{ error?: boolean }>`
    display: flex;
    width: 100%;
    height: 200px;
    border-radius: 12px;
    cursor: pointer;
    align-items: center;
    justify-content: center;

    border: ${({ theme, error }) => `1px solid ${error ? theme.colors.error[650] : theme.colors.black[200]}`};
    box-shadow:
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26);
    margin-block-end: 8px;
`;
const LoadingContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const LabelLoading = styled.span`
    font-size: 14px;
`;
const RecordActionArea = styled(CardActionArea)`
    height: 100%;
    display: flex;
    flex-direction: column;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[0]};
    }
`;

const RecordActionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 15px 0 30px;
`;

const RecordActionButtonsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
    width: 100%;
`;

const RecordTitle = styled.div`
    font-size: 20px;
    color: ${({ theme }) => theme.colors.black.main};
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: bold;
    margin: 1rem 2rem;
`;

const RecordText = styled.div`
    font-size: 14px;
    text-align: center;
`;

const RecordingTime = styled.div`
    color: ${({ theme }) => theme.colors.primary.main};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    font-weight: 700;

    font-size: 18px;
`;

const SpacePlaceholder = styled.div`
    width: 300px;
    justify-content: center;
    align-items: center;
    display: flex;
    flex-direction: row;
`;

const StyledIconButton = styled(IconButton)`
    border: 1px solid ${({ theme }) => theme.colors.black[200]};
    width: 40px;
`;

const Loading = styled(Player)`
    height: 8rem;
    width: 8rem;
`;

const MicrophoneError = styled.span`
    color: ${({ theme }) => theme.colors.error[650]};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 125% */
    margin-block-end: 8px;
`;

const padDigits = (num: number): string => {
    return num.toString().padStart(2, '0');
};

const formatSeconds = (seconds: number): string => {
    const minutes: number = Math.floor(seconds / 60);
    const remainingSeconds: number = seconds % 60;
    return `${padDigits(minutes)}:${padDigits(remainingSeconds)}`;
};

interface Props {
    onRecordComplete: (blob: Blob) => void;
    isUploading?: boolean;
    setIsRecording?: (isRecording: boolean) => void;
}

export default function ({ onRecordComplete, isUploading, setIsRecording }: Props) {
    const theme = useTheme();
    const [isCanceled, setIsCanceled] = useState<boolean>(false);
    const [recordNotAllow, setRecordNotAllow] = useState<boolean>(false);
    const { isAnonymous, loginWithRedirect } = useAuth();
    const mixpanel = useAnalytics();

    const handleNotAllowed = () => {
        setRecordNotAllow(true);
    };

    const { startRecording, stopRecording, isRecording, recordingBlob, recordingTime } = useAudioRecorder(
        {
            noiseSuppression: true,
            echoCancellation: true,
        },
        handleNotAllowed
    );

    const handleStartRecording = () => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-audio', isAnonymous });
            loginWithRedirect({
                screen_hint: 'signup',
                appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo },
            });
        } else {
            setRecordNotAllow(false);
            startRecording();
        }
    };

    useEffect(() => {
        setIsRecording?.(isRecording);
    }, [isRecording]);

    useEffect(() => {
        if (recordingBlob && !isCanceled) {
            onRecordComplete(recordingBlob);
        }
        setIsCanceled(false);
    }, [recordingBlob]);

    const cancelRecording = () => {
        setIsCanceled(true);
        stopRecording();
    };

    // Uploading state
    if (isUploading) {
        return (
            <RecordCard>
                <LoadingContainer>
                    <Loading src={LoadingAnimation} autoplay loop />
                    <LabelLoading>Record is uploading</LabelLoading>
                </LoadingContainer>
            </RecordCard>
        );
    }

    // Idle state
    if (!isRecording) {
        return (
            <>
                <RecordCard error={recordNotAllow}>
                    <RecordActionArea data-testid="record-audio-box" onClick={handleStartRecording}>
                        <MaskedIcon src={RadioChecked} size="32px" color={theme.colors.primary.main} />
                        <RecordTitle>Record audio</RecordTitle>
                        <RecordText>Use your microphone to record</RecordText>
                        <RecordText>what the Avatar will say</RecordText>
                    </RecordActionArea>
                </RecordCard>
                {recordNotAllow && <MicrophoneError>Please allow microphone permissions</MicrophoneError>}
            </>
        );
    }

    // Recording state
    if (isRecording) {
        return (
            <RecordCard>
                <RecordActionWrapper>
                    <RecordingTime>{formatSeconds(recordingTime)}</RecordingTime>
                    <RecordActionButtonsWrapper>
                        <SpacePlaceholder>
                            <StyledIconButton ariaLabel="" onClick={cancelRecording}>
                                <MaskedIcon src={Remove} size="24px" color={theme.colors.error.main} />
                            </StyledIconButton>
                        </SpacePlaceholder>
                        <Tooltip
                            title="Finish recording"
                            placement="top"
                            containerWidth="108px"
                            slotProps={{ tooltip: { sx: { backgroundColor: '#090604', padding: '8px', fontSize: '14px !important' } } }}
                            PopperProps={{
                                modifiers: [{ name: 'offset', options: { offset: [0, -16] } }],
                            }}>
                            <img data-testid="stop-recording-button" src={StopRecording} alt="Stop recording" onClick={stopRecording} />
                        </Tooltip>
                        <SpacePlaceholder />
                    </RecordActionButtonsWrapper>
                </RecordActionWrapper>
            </RecordCard>
        );
    }

    return null;
}
